import { useState, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { X, Upload, FileSpreadsheet } from 'lucide-react';

export function MacquarieUploadDialog({
  open,
  onClose,
  context
}: {
  open: boolean;
  onClose: () => void;
  context?: 'shell' | 'portfolio'
}) {
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [, setLocation] = useLocation();
  const qc = useQueryClient();

  const upload = useMutation({
    mutationFn: async (f: File) => {
      const fd = new FormData();
      fd.append('file', f);
      // We skip the generic json `api` fetch wrapper here because we're sending FormData
      const token = document.cookie.split('; ').find(row => row.startsWith('csrf_token='))?.split('=')[1];
      const res = await fetch('/api/integrations/macquarie/upload-cgt', {
        method: 'POST',
        body: fd,
        headers: {
            'X-CSRF-Token': token || '',
        }
      });
      if (!res.ok) {
         const err = await res.json().catch(() => ({}));
         throw new Error(err.message || 'Upload failed');
      }
      return res.json();
    },
    onSuccess: (data) => {
      qc.invalidateQueries();
      onClose();
      if (data.clientId && context === 'shell') {
          setLocation(`/clients/${data.clientId}`);
      }
    },
    onError: (e: Error) => {
      setError(e.message || 'An error occurred during upload.');
    }
  });

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-0">
      <div className="absolute inset-0 bg-ink/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-paper rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between px-6 py-4 qa-hairline-b">
          <h2 className="text-lg font-medium text-ink flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-forest" />
            Upload Macquarie Report
          </h2>
          <button onClick={onClose} className="text-ink-mute hover:text-ink transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          <p className="text-sm text-ink-mute mb-4">
            Upload a Macquarie <b>Unrealised Gains/Losses</b> or <b>Realised Gains/Losses</b> Excel report (.xlsx).
            {context === 'shell' ? ' We will automatically create the client and their accounts if they don\'t exist.' : ' We will update this client\'s portfolio.'}
          </p>

          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${file ? 'border-forest bg-forest/5' : 'border-paper-trim hover:border-forest/50'}`}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept=".xlsx,.xls"
              onChange={(e) => {
                  if (e.target.files?.[0]) {
                      setFile(e.target.files[0]);
                      setError('');
                  }
              }}
            />
            {file ? (
              <div>
                <p className="text-sm font-medium text-ink">{file.name}</p>
                <p className="text-xs text-ink-mute mt-1">{(file.size / 1024).toFixed(1)} KB</p>
              </div>
            ) : (
              <div className="text-ink-mute flex flex-col items-center gap-2">
                <Upload className="w-8 h-8 opacity-50" />
                <p className="text-sm">Click to select an .xlsx file</p>
              </div>
            )}
          </div>

          {error && <p className="text-sm text-rust mt-4">{error}</p>}
        </div>

        <div className="px-6 py-4 bg-paper-subtle qa-hairline-t flex justify-end gap-3">
          <button onClick={onClose} className="qa-btn-secondary">Cancel</button>
          <button
            className="qa-btn-primary flex items-center gap-2"
            disabled={!file || upload.isPending}
            onClick={() => {
                if (file) upload.mutate(file);
            }}
          >
            {upload.isPending ? 'Uploading...' : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  );
}
